import React, { useEffect, useState, useCallback } from 'react';
import { MessageCircle, HelpCircle } from 'lucide-react';
import { getPendingReminders } from '../services/reminders.api';

interface RemindersQuickAccessProps {
  professionalId: number;
  onOpen: () => void;
  onOpenGuide?: () => void;
  className?: string;
  /** Señal externa para re-fetch (ej. incrementar un número cada vez que se cierra el panel) */
  refreshKey?: number;
}

/**
 * Acceso rápido al panel de Recordatorios, pensado para mostrarse al tope del dashboard
 * en paralelo con ShareProfileButton. Muestra un badge rojo con el total de pendientes
 * (para mañana + para hoy) cuando hay turnos por avisar.
 */
const RemindersQuickAccess: React.FC<RemindersQuickAccessProps> = ({
  professionalId,
  onOpen,
  onOpenGuide,
  className = '',
  refreshKey = 0,
}) => {
  const [pendingTomorrow, setPendingTomorrow] = useState(0);
  const [pendingToday, setPendingToday] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchPending = useCallback(async () => {
    if (!professionalId) return;
    try {
      const data = await getPendingReminders(professionalId);
      // Contamos sólo los que aún no fueron marcados como enviados/respondidos.
      const pendingFilter = (r: any) => !r.manual_last_status;
      const tomorrow = (data.forTomorrow || []).filter(pendingFilter).length;
      const today = (data.forToday || []).filter(pendingFilter).length;
      setPendingTomorrow(tomorrow);
      setPendingToday(today);
    } catch (err) {
      // No rompemos la UI si falla, sólo lo ocultamos.
      console.warn('[RemindersQuickAccess] error fetching pending', err);
    } finally {
      setLoading(false);
    }
  }, [professionalId]);

  useEffect(() => {
    fetchPending();
    const id = window.setInterval(fetchPending, 60_000);
    return () => window.clearInterval(id);
  }, [fetchPending, refreshKey]);

  const total = pendingTomorrow + pendingToday;
  const hasPending = total > 0;

  const subtext = loading
    ? 'Cargando…'
    : hasPending
    ? `${pendingTomorrow} para mañana · ${pendingToday} para hoy`
    : 'Todo al día — sin pendientes';

  return (
    <div
      // text-left: la app centra todo por defecto (.App de CRA), y en la
      // computadora "Todo al día — sin pendientes" quedaba corrido al medio
      className={`bg-white/60 backdrop-blur-sm rounded-2xl p-5 border border-gray-100 shadow-sm text-left ${className}`}
    >
      <div className="flex items-center gap-2.5">
        {/* Icono + badge pendientes */}
        <div className="relative">
          <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl flex items-center justify-center">
            <MessageCircle className="w-4 h-4 text-white" />
          </div>
          {hasPending && (
            <span
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full ring-2 ring-white animate-pulse"
              aria-label={`${total} recordatorios pendientes`}
            >
              {total > 99 ? '99+' : total}
            </span>
          )}
        </div>

        {/* Título + estado */}
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 tracking-tight flex items-center gap-2">
            Recordatorios
            {hasPending && (
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-red-600">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                {total} pendiente{total === 1 ? '' : 's'}
              </span>
            )}
          </h3>
          <p className="text-[11px] text-gray-500 truncate">{subtext}</p>
        </div>

        {/* Acciones */}
        {onOpenGuide && (
          <button
            onClick={onOpenGuide}
            className="flex-shrink-0 p-2 rounded-xl bg-gray-100 text-gray-600 hover:bg-gray-200 active:scale-95 transition-all"
            title="¿Cómo funcionan los recordatorios?"
            aria-label="Ver guía de recordatorios"
          >
            <HelpCircle className="w-4 h-4" />
          </button>
        )}

        <button
          onClick={onOpen}
          className={`flex-shrink-0 flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
            hasPending
              ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-md shadow-emerald-500/25'
              : 'bg-gray-900 text-white hover:bg-gray-800 active:scale-95'
          }`}
        >
          <MessageCircle className="w-3.5 h-3.5" />
          <span>Abrir</span>
        </button>
      </div>
    </div>
  );
};

export default RemindersQuickAccess;
